import { forwardRef, type ReactNode } from "react";

import { type TabPaneProps, Tabs, type TabsProps } from "antd";

import { cn } from "@btnet/utilities-core";

import "./BuiTabs.less";

/**
 * Props for the BuiTabs component.
 *
 * This is a minimal wrapper around antd Tabs (4.16.12) with BDS patterns.
 * All antd TabsProps are passed through for maximum composability, except for type, and size.  Design does not want that functionality exposed by 'type' to be used and 'size' can be accounted for by using BT padding/font-size tokens if need be.
 */
export interface IBuiTabsProps extends Omit<TabsProps, "id" | "type" | "size"> {
  /**
   * Required test identifier for testing and analytics
   */
  testid: string;

  /**
   * Optional unique identifier for the tabs container
   */
  id?: string;

  /**
   * Optional CSS class name(s)
   */
  className?: string;

  /**
   * Tab pane children (BuiTabPane components)
   */
  children?: ReactNode;

  /**
   * Currently active tab key (controlled mode)
   */
  activeKey?: string;

  /**
   * Initial active tab key (uncontrolled mode)
   */
  defaultActiveKey?: string;

  /**
   * Callback when active tab changes
   */
  onChange?: (activeKey: string) => void;

  /**
   * Callback when tab is clicked
   */
  onTabClick?: (
    key: string,
    event: React.MouseEvent | React.KeyboardEvent,
  ) => void;

  /**
   * This controls the position of the tab relative to the location of its pane
   * @default "top"
   */
  tabPosition?: "top" | "bottom" | "left" | "right";

  /**
   * Whether to center tabs within their container
   * @default false
   */
  centered?: boolean;

  /**
   * Enable tab transition animations
   * @default { inkBar: true, tabPane: false }
   */
  animated?: boolean | { inkBar: boolean; tabPane: boolean };

  /**
   * Callback for tab add/remove (editable-card type)
   */
  onEdit?: (
    targetKey: string | React.MouseEvent | React.KeyboardEvent,
    action: "add" | "remove",
  ) => void;

  /**
   * Destroys the component when another TabPane becomes active
   * @default false
   */
  destroyInactiveTabPane?: boolean;

  /**
   * Gutter between tabs
   */
  tabBarGutter?: number;

  /**
   * Extra content in tab bar
   */
  tabBarExtraContent?: ReactNode | { left?: ReactNode; right?: ReactNode };

  /**
   * Custom tab bar style
   */
  tabBarStyle?: React.CSSProperties;

  style?: React.CSSProperties;
}

/**
 * Props for the BuiTabPane component.
 *
 * This is a minimal wrapper around antd Tabs.TabPane with BDS patterns.
 * All antd TabPaneProps are passed through for maximum composability, except for disabled.  If a pane would be disabled, it should instead not be rendered.
 */
export interface IBuiTabPaneProps
  extends Omit<TabPaneProps, "closeIcon" | "disabled"> {
  /**
   * Tab label - accepts any ReactNode for composability
   */
  tab?: ReactNode;

  /**
   * Unique key to identify this tab (required)
   */
  key: string;

  /**
   * Force render of tab pane (prevents lazy loading)
   * @default false
   */
  forceRender?: boolean;

  /**
   * Tab pane content - accepts any ReactNode for composability
   */
  children?: ReactNode;
}

/**
 * BuiTabPane is a minimal wrapper around antd Tabs.TabPane.
 * Represents an individual tab panel within BuiTabs.
 * Accepts any ReactNode for maximum composability.
 * @param props - The tab pane properties
 * @param props.children - The content to render inside the tab pane
 * @example
 * ```tsx
 * <BuiTabs.TabPane
 *   tab={<><BdsIcon icon="home" /> Home</>}
 *   key="home"
 *   disabled={false}
 * >
 *   Home content
 * </BuiTabs.TabPane>
 * ```
 */
const BuiTabPane = function BuiTabPane({
  children,
  ...props
}: IBuiTabPaneProps) {
  return <Tabs.TabPane {...props}>{children}</Tabs.TabPane>;
};

/**
 * BuiTabs is a minimal wrapper around antd Tabs (4.16.12) with BDS patterns.
 * Provides composable tab navigation with support for:
 * - Controlled and uncontrolled modes
 * - Multiple tab positions (top, bottom, left, right)
 * - Icons and custom content in tabs
 * - Disabled tabs
 * - Editable tabs (add/remove)
 *
 * For WAI-ARIA keyboard navigation, use the `useTabsKeyboardNavigation` hook
 * with a ref passed to this component.
 * @param props - The tabs component properties
 * @param props.testid - Test ID for testing purposes
 * @param props.id - Unique identifier for the tabs component
 * @param props.className - Additional CSS classes to apply
 * @param props.children - Tab pane children
 * @param props.tabPosition - Position of the tabs (top, bottom, left, right)
 * @example
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * useTabsKeyboardNavigation({ containerRef: ref, tabPosition: "top" });
 *
 * <BuiTabs ref={ref} testid="my-tabs" defaultActiveKey="1">
 *   <BuiTabs.TabPane tab="Tab 1" key="1">
 *     Content 1
 *   </BuiTabs.TabPane>
 *   <BuiTabs.TabPane tab="Tab 2" key="2">
 *     Content 2
 *   </BuiTabs.TabPane>
 * </BuiTabs>
 * ```
 */
const BuiTabsComponent = forwardRef<HTMLDivElement, IBuiTabsProps>(
  ({ testid, id, className, children, tabPosition = "top", ...props }, ref) => {
    return (
      <div
        ref={ref}
        id={id}
        data-testid={testid}
        className={cn("bui-tabs", className)}
      >
        <Tabs {...props} tabPosition={tabPosition}>
          {children}
        </Tabs>
      </div>
    );
  },
);

BuiTabsComponent.displayName = "BuiTabs";

type BuiTabsType = typeof BuiTabsComponent & {
  TabPane: typeof BuiTabPane;
};

export const BuiTabs = BuiTabsComponent as BuiTabsType;
BuiTabs.TabPane = BuiTabPane;
